/**
 * Offline upload queue (brief §5.3, §8).
 *
 * A photo taken in a dead zone on the side of a highway must not be lost, so
 * every capture is written to IndexedDB *before* any network call. The queue
 * drains opportunistically and on `online`; nothing is deleted until the
 * server has acknowledged it.
 */

const DB_NAME = 'harkey-portal';
const DB_VERSION = 1;
const STORE = 'uploads';

export interface QueuedUpload {
  id: string;
  token: string;
  category: string;
  contentType: string;
  bytes: ArrayBuffer;
  sha256: string;
  capturedAt: number;
  attempts: number;
  lastError: string | null;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Could not open IndexedDB'));
  });
}

async function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const transaction = db.transaction(STORE, mode);
    const request = fn(transaction.objectStore(STORE));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
    transaction.oncomplete = () => db.close();
  });
}

/** SHA-256 of the bytes, which is what makes a retry idempotent server-side. */
export async function hashBytes(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function enqueueUpload(
  item: Omit<QueuedUpload, 'id' | 'attempts' | 'lastError'>,
): Promise<QueuedUpload> {
  const record: QueuedUpload = {
    ...item,
    id: crypto.randomUUID(),
    attempts: 0,
    lastError: null,
  };
  await tx('readwrite', (store) => store.put(record));
  return record;
}

export async function listQueued(): Promise<QueuedUpload[]> {
  return tx<QueuedUpload[]>('readonly', (store) => store.getAll() as IDBRequest<QueuedUpload[]>);
}

export async function removeQueued(id: string): Promise<void> {
  await tx('readwrite', (store) => store.delete(id));
}

async function markFailure(item: QueuedUpload, error: string): Promise<void> {
  await tx('readwrite', (store) =>
    store.put({ ...item, attempts: item.attempts + 1, lastError: error }),
  );
}

export interface DrainResult {
  uploaded: number;
  remaining: number;
  lastError: string | null;
}

/**
 * Attempts every queued upload once.
 *
 * A 4xx other than 429 is permanent — the bytes will never become acceptable —
 * so the item is dropped rather than retried forever. Anything else is left in
 * the queue for the next drain.
 */
export async function drainQueue(): Promise<DrainResult> {
  const queued = await listQueued();
  let uploaded = 0;
  let lastError: string | null = null;

  for (const item of queued) {
    const params = new URLSearchParams({
      category: item.category,
      sha256: item.sha256,
      capturedAt: String(item.capturedAt),
    });

    try {
      const res = await fetch(`/api/portal/${item.token}/photos?${params}`, {
        method: 'POST',
        headers: { 'Content-Type': item.contentType },
        body: item.bytes,
      });

      if (res.ok) {
        await removeQueued(item.id);
        uploaded++;
        continue;
      }

      const message = `Upload rejected (${res.status})`;
      if (res.status >= 400 && res.status < 500 && res.status !== 429) {
        // Permanent: keep the queue from filling with a photo that can never
        // be accepted, but surface why.
        await removeQueued(item.id);
        lastError = message;
        continue;
      }
      lastError = message;
      await markFailure(item, message);
    } catch (err) {
      lastError = err instanceof Error ? err.message : String(err);
      await markFailure(item, lastError);
    }
  }

  return { uploaded, remaining: (await listQueued()).length, lastError };
}

/**
 * Downscales and re-encodes before upload. Phone photos are 5 MB and a crew's
 * connection on a highway shoulder is not.
 */
export async function compressImage(
  file: File,
  maxDimension = 2000,
  quality = 0.82,
): Promise<{ bytes: ArrayBuffer; contentType: string }> {
  const passthrough = async (): Promise<{ bytes: ArrayBuffer; contentType: string }> => ({
    bytes: await file.arrayBuffer(),
    contentType: file.type || 'image/jpeg',
  });

  if (typeof createImageBitmap !== 'function' || typeof OffscreenCanvas !== 'function') {
    return passthrough();
  }

  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = new OffscreenCanvas(width, height);
    const context = canvas.getContext('2d');
    if (!context) return passthrough();

    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality });
    return { bytes: await blob.arrayBuffer(), contentType: 'image/jpeg' };
  } catch {
    // Never lose the photo because compression failed.
    return passthrough();
  }
}
